'use client';

import { TFunction } from 'i18next';
import { AcceptWithdrawalOfferMutation } from '@bts-web/data-layer/server';
import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import {
  getPrecisionFloatValue,
  getRoundedPrecision,
  intlAssetValueFormatting,
  intlFiatValueFormatting,
  PrecisionFloat,
} from '@bts-web/utils-formatting';
import { getAppConfig } from '@bts-web/core-app-config';
import { getCryptoTransferTranslations } from '../../../../utils/getCryptoTransferTranslations';
import { SuccessModal } from '../../../../../common';
import { ModalComponent } from '../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { AssetType } from '@bts-web/core-features/asset-details';
import { isBpfsAsset } from '@bts-web/core-features/trade';

export type WithdrawalSuccessModalProps = {
  withdrawalData: {
    asset?: NonNullable<
      AcceptWithdrawalOfferMutation['response']['acceptCryptoWithdrawal']
    >['asset'];
    assetAmount?: PrecisionFloat;
    fiatAmount?: PrecisionFloat;
  };
};

export const WithdrawalSuccessModal: ModalComponent<
  WithdrawalSuccessModalProps
> = ({ withdrawalData, onClose }) => {
  const { currency } = getAppConfig();

  const locale = useClientLocale();

  const { t } = useClientTranslation();

  const assetAmount = intlAssetValueFormatting(
    getPrecisionFloatValue(withdrawalData.assetAmount),
    {
      locale,
      fractionDigits: withdrawalData?.assetAmount?.precision,
    },
  );

  const fiatAmount = intlFiatValueFormatting(
    getRoundedPrecision(withdrawalData.fiatAmount),
    {
      locale,
      currency,
      fractionDigits: withdrawalData?.fiatAmount?.precision,
    },
  );

  const description = t('crypto_withdrawal_success_body', {
    assetAmount: `${assetAmount} ${withdrawalData.asset?.symbol}`,
    fiatAmount,
  });

  const translations = getCryptoTransferTranslations(t as TFunction);

  const logoUrl = withdrawalData.asset?.logoUrl as string;

  const logoTitle = withdrawalData.asset?.name as string;

  const isBpfs = isBpfsAsset(withdrawalData.asset?.__typename as AssetType);

  return (
    <SuccessModal
      title={translations.cryptoWithdrawalSuccessSubtitle}
      description={description}
      logo={{ url: logoUrl, name: logoTitle }}
      onClose={onClose}
      goToPortfolioBtnVariant="primary"
      disableOverlayClose
      disableCloseButton
      isBpfs={isBpfs}
    />
  );
};
